import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, LinearProgress, Button } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { PlainTextField } from "components";
import { patchConfirmChangeEmail, patchChangeEmail } from "api/user";
import i18n from "./i18n";

// prop types
interface ChangeEmailDialogProps {
    isAlias: boolean,
    show: boolean,
    onClose: () => void,
}

// change email dialog
const ChangeEmailDialog = ({ isAlias, show, onClose }: ChangeEmailDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [code, setCode] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [emailAddress, setEmailAddress] = useState("");
    const [isLoading, setLoading] = useState(false);

    // did hide hook -> clear input fields
    useEffect(() => {
        if (!show) {
            setCode("");
            setCodeSent(false);
            setEmailAddress("");
        }
    }, [show]);

    // execute action
    const execute = async () => {
        setLoading(true);
        try {
            if (codeSent) {
                await patchConfirmChangeEmail({ confirmationCode: code });
                enqueueSnackbar(i18n.savedSuccessfully, { variant: "success" });
                onClose();
            } else {
                await patchChangeEmail({ newEmail: emailAddress });
                setCodeSent(true);
            }
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // render
    return (
        <Dialog fullWidth open={show} onClose={onClose}>
            <DialogTitle>{i18n.changeEmailAddress}</DialogTitle>
            <DialogContent dividers>

                {/* description text */}
                {codeSent && (
                    <DialogContentText>{i18n.changeEmailAddressCodeSent}</DialogContentText>
                )}

                {/* new email */}
                <PlainTextField
                    autoFocus
                    disabled={isLoading || codeSent}
                    helperText={isAlias ? i18n.aliasEmailChange : ""}
                    label={i18n.newEmailAddress}
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value?.toLowerCase())}
                />

                {/* code */}
                {codeSent && (
                    <PlainTextField
                        disabled={isLoading}
                        label={i18n.code}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                    />
                )}

            </DialogContent>
            {isLoading && <LinearProgress />}
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" disabled={isLoading} onClick={execute}>
                    {i18n.ok}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeEmailDialog;
